import * as React from 'react';
import Icon, { Icons } from '@app/common/components/Icon';
import cx from 'classnames';
import UploadADocDropdown from '@app/components/dropdowns/UploadADocDropdown';
import { ReactComponent as ExpandIcon } from '../../../../../../../assets/images/icons/expand.svg';
import Tooltip from '@app/common/components/tooltips/Tooltip';
import useComponentId from '@app/hooks/useComponentId';
import { isUploadedDocument as isUploadedDocumentType, UploadedDocument } from '@app/types/UploadADoc';
import ClientAnnotationCounts from './ClientAnnotationCounts';
import Card, { CardShadow, CardStyles } from '@app/common/components/cards/Card';
import { Tag, TagSize } from '@app/common/components/tag/Tag';
import { useSortable } from '@dnd-kit/sortable';
import Button from '@app/common/components/Button';

import styles from './Document.module.scss';
import { CSS } from '@dnd-kit/utilities';

interface DocumentProps {
    document: UploadedDocument;
    clients: unknown[];
    documentIndex: number;
    deleteDocument: (id: string) => Promise<void>;
    documentsCount: number;
    loading?: boolean;
    viewDocumentLink?: string;
    documentTypeDisplayName?: string;
    isDraggable?: boolean;
    isDragDisabled?: boolean;
    isDragging?: boolean;
}

export const Document: React.FC<DocumentProps> = ({
    document,
    clients,
    documentIndex,
    deleteDocument,
    documentsCount,
    viewDocumentLink,
    documentTypeDisplayName,
    isDraggable,
    isDragDisabled,
    isDragging
}) => {
    const radixDropdownPortalContainerRef = React.useRef<HTMLDivElement | null>(null);
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: document.id,
        disabled: isDragDisabled
    });
    const tooltipId = useComponentId().toString();

    const isUploadedDocument = isUploadedDocumentType(document);

    return (
        <Card
            className={cx(styles.container, { [styles.dragging]: isDragging })}
            data-test={`custom-document-${documentIndex}`}
            shadow={CardShadow.SMALL}
            animate={false}
            style={CardStyles.SQUARE}
            ref={setNodeRef}
            customStyles={{ '--dragTransform': CSS.Transform.toString(transform), '--dragTransition': transition }}
        >
            {isDraggable && (
                <Button
                    type="button"
                    {...attributes}
                    {...listeners}
                    className={cx(styles.dragHandle, {
                        [styles.dragging]: isDragging,
                        [styles.dragDisabled]: isDragDisabled
                    })}
                >
                    <Icon icon={Icons.DRAG} />
                </Button>
            )}
            <Icon icon={Icons.PDF} className={styles.icon} />
            <div className={styles.details}>
                {isUploadedDocument ? (
                    <>
                        <a
                            className={cx(styles.name, styles.link)}
                            href={viewDocumentLink}
                            target="_blank"
                            rel="noreferrer"
                            data-tip={document.document?.documentName}
                            data-for={tooltipId}
                        >
                            {document.document.documentName}
                        </a>
                        <div className={styles.size}>{document.document.prettyBytes}</div>
                    </>
                ) : (
                    <div className={styles.name} data-tip={document.document?.documentName} data-for={tooltipId}>
                        {document.document.documentName}
                    </div>
                )}
            </div>
            <Tooltip place="top" effect="solid" id={tooltipId} />
            {documentTypeDisplayName && (
                <Tag className={styles.category} text={documentTypeDisplayName} size={TagSize.X_Small} />
            )}
            <div className={styles.placeholders}>
                <ClientAnnotationCounts document={document} clients={clients} />
            </div>
            <div ref={radixDropdownPortalContainerRef} className={styles.actions}>
                {isUploadedDocument && (
                    <UploadADocDropdown
                        uploadedDocument={document}
                        removeFileToUpload={() => deleteDocument(document.id)}
                        uploadedDocumentsCount={documentsCount}
                        radixDropdownPortalContainer={radixDropdownPortalContainerRef.current || undefined}
                        index={documentIndex}
                    >
                        <ExpandIcon className={styles.expandIcon} data-test={`document-${documentIndex}-dropdown`} />
                    </UploadADocDropdown>
                )}
            </div>
        </Card>
    );
};

export default Document;
